// FollowUpEmailContainer.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';

import {
    Grid,
    Typography,
    // Switch removed - using TeamIosDarkToggle instead
    Box,
    Tooltip,
    Chip,
    CircularProgress
} from '@mui/material';
import { InfoOutlinedIcon } from 'icons/materialIcons';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import { useCardContext } from 'contexts/CardContext';
import { useUserPreferences } from 'contexts/UserPreferencesContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { updateAutoFollowUpEmail, getCardById } from 'apis/cardApi';

const TeamFollowUpEmailContainer = () => {
    const { cardId: paramCardId } = useParams();
    const { cardState, setAutoFollowUpIntroEmail } = useCardContext();
    const { authState } = useAuth();
    const userId = authState.user?.userId;
    const location = useLocation();

    // ✅ TEMPLATE CONTEXT: Use authenticated user's card if no cardId from params
    // Check if we're in template edit page by checking the URL path
    const isTemplateContext = !paramCardId || location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId || 
                      (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = isTemplateContext ? userCardId : paramCardId;

    // Use the user preferences context
    const { getDefaultFollowUpEmail } = useUserPreferences();

    const [isInitialized, setIsInitialized] = useState(false);
    const [isUsingDefault, setIsUsingDefault] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch card data and initialize settings
    useEffect(() => {
        const fetchCardData = async () => {
            if (!cardId || isInitialized) return;

            try {
                setIsLoading(true);

                // 1. Get user default from context
                const defaultSetting = getDefaultFollowUpEmail(userId);

                // 2. Get card-specific setting
                // For template context, try to get the actual setting from server
                if (isTemplateContext) {
                    try {
                        const response = await getCardById(cardId);
                        if (response && response.card) {
                            const cardSetting = response.card.autoFollowUpIntroEmail;
                            // If card has a specific setting defined, use it
                            if (cardSetting !== undefined && cardSetting !== null) {
                                setAutoFollowUpIntroEmail(cardId, cardSetting);
                                setIsUsingDefault(false);
                            } else {
                                // Otherwise use the default
                                setAutoFollowUpIntroEmail(cardId, defaultSetting);
                                setIsUsingDefault(true);
                            }
                        } else {
                            // Fallback to default if card not found
                            setAutoFollowUpIntroEmail(cardId, defaultSetting);
                            setIsUsingDefault(true);
                        }
                    } catch (error) {
                        console.log('Template context - error fetching, using default value');
                        setAutoFollowUpIntroEmail(cardId, defaultSetting);
                        setIsUsingDefault(true);
                    }
                } else {
                    // Regular card context
                    const response = await getCardById(cardId);

                    if (response && response.card) {
                        const cardSetting = response.card.autoFollowUpIntroEmail;

                        // If card has a specific setting defined, use it
                        if (cardSetting !== undefined && cardSetting !== null) {
                            setAutoFollowUpIntroEmail(cardId, cardSetting);
                            setIsUsingDefault(false);
                        } else {
                            // Otherwise use the default
                            setAutoFollowUpIntroEmail(cardId, defaultSetting);
                            setIsUsingDefault(true);
                        }
                    }
                }

                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to load follow-up email settings:', error);
                // Fallback to default on error
                if (cardId) {
                    const defaultSetting = getDefaultFollowUpEmail(userId);
                    setAutoFollowUpIntroEmail(cardId, defaultSetting);
                    setIsUsingDefault(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchCardData();
    }, [cardId, userId, isInitialized, setAutoFollowUpIntroEmail, getDefaultFollowUpEmail, isTemplateContext]);

    // Keep card setting in sync with user default when using default
    useEffect(() => {
        if (isUsingDefault && isInitialized && !isLoading) {
            const defaultSetting = getDefaultFollowUpEmail(userId);
            setAutoFollowUpIntroEmail(cardId, defaultSetting);
        }
    }, [isUsingDefault, cardId, userId, setAutoFollowUpIntroEmail, getDefaultFollowUpEmail, isInitialized, isLoading]);

    // Memoized toggle handler
    const handleToggle = useCallback(() => {
        if (!cardId) {
            console.error('Card ID is required to update follow-up email settings');
            return;
        }

        const newValue = !cardState[cardId]?.autoFollowUpIntroEmail;
        console.log("Toggle clicked, new value:", newValue);

        // Update local state first for immediate feedback
        setAutoFollowUpIntroEmail(cardId, newValue);

        // Update on the server
        updateAutoFollowUpEmail(cardId, newValue)
            .then(() => {
                console.log("API call successful");
                setIsUsingDefault(false); // Now using custom setting, not default

                setSnackbar({
                    open: true,
                    message: newValue
                        ? 'Follow-up emails enabled for this card'
                        : 'Follow-up emails disabled for this card',
                    severity: 'success'
                });
            })
            .catch(error => {
                console.error("API call failed:", error);
                // Revert on error
                setAutoFollowUpIntroEmail(cardId, !newValue);

                setSnackbar({
                    open: true,
                    message: 'Failed to update follow-up email setting',
                    severity: 'error'
                });
            });
    }, [cardId, cardState, setAutoFollowUpIntroEmail]);

    // Reset to user default
    const handleResetToDefault = useCallback(() => {
        if (!cardId) return;

        const defaultSetting = getDefaultFollowUpEmail(userId);

        // First update local state for immediate feedback
        setAutoFollowUpIntroEmail(cardId, defaultSetting);
        setIsUsingDefault(true);

        // Then update the server (using null to indicate "use default")
        updateAutoFollowUpEmail(cardId, null)
            .then(() => {
                console.log("Reset to default successful");

                setSnackbar({
                    open: true,
                    message: 'Card now uses your default setting for follow-up emails',
                    severity: 'success'
                });
            })
            .catch(error => {
                console.error("Failed to reset to default:", error);
                // Revert to previous state on error
                setIsUsingDefault(false);

                setSnackbar({
                    open: true,
                    message: 'Failed to reset to default setting',
                    severity: 'error'
                });
            });
    }, [cardId, userId, getDefaultFollowUpEmail, setAutoFollowUpIntroEmail]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header */}
            <Box sx={{
                p: 2,
                backgroundColor: 'white',
                flexShrink: 0,
            }}>
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                    }}
                >
                    Follow Up Email
                </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 2,
                    backgroundColor: '#ffffff'
                }}
            >
                <Grid container spacing={2}>
                    {/* Toggle and Description */}
                    <Grid item xs={12} container alignItems="center" spacing={1}>
                        <Grid item xs={10}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: "#4f4f4f"
                                    }}
                                >
                                    Enable Follow-up Emails
                                </Typography>
                                <Tooltip
                                    title="When enabled, an email will automatically be sent to connect both parties after a lead submits their information"
                                    placement="top"
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '16px',
                                            marginLeft: 1,
                                            color: '#9e9e9e',
                                            cursor: 'help'
                                        }}
                                    />
                                </Tooltip>

                                {isUsingDefault && !isLoading && (
                                    <Chip
                                        label="Using default"
                                        size="small"
                                        sx={{
                                            ml: 1,
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            backgroundColor: '#f0f0f0'
                                        }}
                                    />
                                )}
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{ fontSize: '0.75rem', fontWeight: 600, color: "#828282", marginLeft: 0 }}
                            >
                                When Follow Up Email is enabled, an email intro will automatically be sent after connecting with a new lead. You can edit the email intro and the time it is sent below.
                            </Typography>

                            {isUsingDefault && !isLoading && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.7rem',
                                        color: "#9e9e9e",
                                        fontStyle: 'italic',
                                        mt: 1
                                    }}
                                >
                                    This card is using your account default setting. You can override it for this card specifically.
                                </Typography>
                            )}

                            {!isUsingDefault && !isLoading && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.7rem',
                                        color: "#2196f3",
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        mt: 1
                                    }}
                                    onClick={handleResetToDefault}
                                >
                                    {/* Reset to my account default */}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={2} display="flex" justifyContent="flex-end">
                            {isLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <div onClick={handleToggle} style={{ cursor: 'pointer' }}>
                                    <TeamIosDarkToggle
                                        checked={Boolean(cardState[cardId]?.autoFollowUpIntroEmail)}
                                        onChange={handleToggle}
                                    />
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </Box>
    );
};

export default TeamFollowUpEmailContainer;